import { useEffect, useRef, useState } from "react";
import type { EventRow } from "../api/types";
import { ChevronDownIcon } from "./Icon";

function levelClass(level: EventRow["level"]): string {
  if (level === "error") return "text-danger";
  if (level === "warn") return "text-warn";
  return "text-text";
}

function formatTime(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleTimeString(undefined, { hour12: false });
}

export default function EventLog({ events, defaultOpen = false }: { events: EventRow[]; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [events, open]);

  return (
    <div className="rounded-lg border border-border bg-surface">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-text"
        aria-expanded={open}
      >
        <span>Event log{events.length ? ` (${events.length})` : ""}</span>
        <ChevronDownIcon width={16} height={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open ? (
        <div
          ref={scrollRef}
          className="max-h-72 overflow-y-auto border-t border-border px-4 py-3 font-mono text-xs leading-relaxed"
        >
          {events.length === 0 ? (
            <p className="text-muted">No events yet.</p>
          ) : (
            events.map((ev) => (
              <div key={ev.id} className="flex gap-2 py-0.5">
                <span className="shrink-0 text-muted">{formatTime(ev.ts)}</span>
                {ev.stage ? <span className="shrink-0 text-muted">[{ev.stage}]</span> : null}
                <span className={levelClass(ev.level)}>{ev.message}</span>
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
